<script setup lang="ts">
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { darkTheme } from 'naive-ui';
import {
  NConfigProvider,
  NMessageProvider,
  NDialogProvider,
  NLayout,
  NLayoutHeader,
  NLayoutContent,
  NSpace,
  NButton,
  NText,
} from 'naive-ui';
import { useAuthStore } from '@/stores/auth';

const router = useRouter();
const auth = useAuthStore();
const showNav = computed(() => auth.isAuthenticated && router.currentRoute.value.name !== 'Login');

function handleLogout() {
  auth.logout();
  router.push('/login');
}
</script>

<template>
  <NConfigProvider :theme="darkTheme">
    <NMessageProvider>
      <NDialogProvider>
        <NLayout class="app-layout">
          <!-- 顶栏 -->
          <NLayoutHeader v-if="showNav" class="app-header" bordered>
            <div class="header-left">
              <span class="logo-text">OPTIBYTE</span>
              <span class="logo-badge">ADMIN</span>
            </div>
            <NSpace align="center" :size="16">
              <NButton
                text
                :class="{ 'nav-active': $route.name === 'RedeemCards' }"
                @click="router.push('/redeem-cards')"
              >
                点卡管理
              </NButton>
              <div class="header-divider" />
              <NText depth="3" style="font-size: 13px">
                {{ auth.user?.email || auth.user?.nickname }}
              </NText>
              <NButton size="small" quaternary @click="handleLogout">退出</NButton>
            </NSpace>
          </NLayoutHeader>

          <!-- 主体 -->
          <NLayoutContent class="app-content" :class="{ 'with-header': showNav }">
            <RouterView />
          </NLayoutContent>
        </NLayout>
      </NDialogProvider>
    </NMessageProvider>
  </NConfigProvider>
</template>

<style>
/* ===== Global Reset ===== */
*,
*::before,
*::after {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

html,
body,
#app {
  height: 100%;
  font-family: 'Outfit', sans-serif;
  background: #0a0a0f;
  color: #e0e0e6;
  -webkit-font-smoothing: antialiased;
}

/* ===== Layout ===== */
.app-layout {
  min-height: 100vh;
  background: #0a0a0f !important;
}

.app-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 28px;
  height: 56px;
  background: rgba(14, 14, 22, 0.95) !important;
  backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06) !important;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.logo-text {
  font-family: 'JetBrains Mono', monospace;
  font-weight: 700;
  font-size: 16px;
  letter-spacing: 2px;
  color: #ffffff;
}

.logo-badge {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 1.5px;
  padding: 2px 8px;
  border-radius: 3px;
  background: linear-gradient(135deg, #22c55e, #16a34a);
  color: #000;
}

.header-divider {
  width: 1px;
  height: 20px;
  background: rgba(255, 255, 255, 0.1);
}

.nav-active {
  color: #22c55e !important;
}

.app-content {
  background: #0a0a0f !important;
  min-height: 100vh;
}

.app-content.with-header {
  padding-top: 56px;
}
</style>
